import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Spin,
  notification,
} from 'antd';
import {
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  SearchOutlined,
  CalculatorOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import lib from '../../assets/utils/lib';
import {
  DATA_SCOPE_OPTIONS,
  DEFAULT_EXPORT_PARAMS,
  OUTPUT_FORMAT_OPTIONS,
  PA_LINE_STATUS_OPTIONS,
  buildOrgPaymentAdviceParams,
  canExportOrgPaymentAdvice,
  isPaApprovedScope,
  isServerRenderedOutputFormat,
  parseReportOptionsFromSchema,
  previewOrgPaymentAdvice,
  queueOrgPaymentAdviceExport,
  fetchOrgPaymentAdviceDataSync,
} from './orgPaymentAdviceHelpers';
import { renderOrgPaymentAdviceFromPayload } from './generateOrgPaymentAdviceExport';
import FinanceReconciliationPanel from '../Finance/FinanceReconciliationPanel';
import FinanceHelpPanel from '../Finance/FinanceHelpPanel';
import FieldGuideLabel from '../Finance/FieldGuideLabel';
import {
  applyPaymentAdviceReconcileToForm,
  buildPaDashboardCompareUrl,
  consumePaymentAdviceReconcileParams,
  getFinanceHelpBlock,
  getPaymentAdviceSummaryMetrics,
  getReconciliationBlock,
  getSchemaHelpForScope,
  mergeFinanceHelp,
  normalizePaDashboardCompareParams,
} from '../../utils/financeReconciliationHelpers';
import '../Finance/finance-reconciliation.css';
import './org-payment-advice-export.css';

const { RangePicker } = DatePicker;

const PERIOD_TYPES_ALL = [
  { value: 'month', label: 'Single month' },
  { value: 'month_range', label: 'Month range' },
  { value: 'date_range', label: 'Date range' },
  { value: 'payment_batch', label: 'Payment batch' },
];

const SEGMENT_OPTIONS = [
  { value: 2, label: 'Pending CFO' },
  { value: 3, label: 'Paid' },
];

const FILE_TYPE_OPTIONS = OUTPUT_FORMAT_OPTIONS.filter((o) => o.value !== 'json');

function buildPeriodFromForm(values) {
  const type = values.period_type || 'month_range';
  if (type === 'month') {
    const m = values.single_month?.format('YYYY-MM');
    if (!m) throw new Error('Select a month');
    return { type, month: m };
  }
  if (type === 'month_range') {
    const from = values.month_range?.[0]?.format('YYYY-MM');
    const to = values.month_range?.[1]?.format('YYYY-MM');
    if (!from || !to) throw new Error('Select a month range');
    return { type, month_from: from, month_to: to };
  }
  if (type === 'date_range') {
    const from = values.date_range?.[0]?.format('YYYY-MM-DD');
    const to = values.date_range?.[1]?.format('YYYY-MM-DD');
    if (!from || !to) throw new Error('Select a date range');
    return { type, date_from: from, date_to: to };
  }
  if (type === 'payment_batch') {
    const batch = values.payment_batch?.trim();
    if (!batch) throw new Error('Enter a payment batch reference');
    return { type, payment_batch: batch };
  }
  throw new Error('Invalid period type');
}

function SummaryMetric({ label, value, wide, currency, helpText, primary, deprecated }) {
  return (
    <div
      className={[
        'opa-export__metric',
        wide ? 'opa-export__metric--wide' : '',
        primary ? 'opa-export__metric--primary' : '',
        deprecated ? 'opa-export__metric--deprecated' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <FieldGuideLabel
        label={label}
        helpText={helpText}
        primary={primary}
        deprecated={deprecated}
        className="opa-export__metric-label"
      />
      <span className={`opa-export__metric-value${currency ? ' opa-export__metric-value--currency' : ''}`}>
        {value}
      </span>
    </div>
  );
}

const OrgPaymentAdviceExportPanel = ({ organization, user, onOpenExportsDrawer }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [schema, setSchema] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(null);

  const organizationId = organization?.org_id ?? organization?.organization_id;
  const orgName = organization?.org_name || 'this organization';
  const canExport = canExportOrgPaymentAdvice(user);

  const reportOptionsDefaults = useMemo(
    () => parseReportOptionsFromSchema(schema),
    [schema],
  );

  const loadSchema = useCallback(async () => {
    setSchemaLoading(true);
    try {
      const resp = await lib.getOrgPaymentAdviceSchema();
      const data = resp?.data?.data;
      if (resp?.data?.error) throw new Error(resp?.data?.message || 'Failed to load report options');
      setSchema(data || null);
    } catch (e) {
      notification.error({
        message: 'Could not load report options',
        description: e?.message,
      });
    } finally {
      setSchemaLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSchema();
  }, [loadSchema]);

  useEffect(() => {
    if (!organizationId || schemaLoading) return;

    const baseValues = {
      period_type: 'month_range',
      month_range: [dayjs().subtract(1, 'month').startOf('month'), dayjs().startOf('month')],
      claim_segment: schema?.default_claim_segment ?? DEFAULT_EXPORT_PARAMS.claim_segment,
      output_format: DEFAULT_EXPORT_PARAMS.output_format,
      report_options: reportOptionsDefaults,
    };

    const stored = consumePaymentAdviceReconcileParams();
    if (stored) {
      if (
        stored.organization_id
        && String(stored.organization_id) !== String(organizationId)
      ) {
        form.setFieldsValue(baseValues);
        return;
      }
      form.setFieldsValue(baseValues);
      applyPaymentAdviceReconcileToForm(form, stored, reportOptionsDefaults);
      notification.info({
        message: 'Filters applied from PA dashboard',
        description: 'Review the report setup, then check totals to compare.',
      });
      return;
    }

    form.setFieldsValue(baseValues);
  }, [organizationId, schemaLoading, form, reportOptionsDefaults, schema]);

  const buildParams = () => {
    const values = form.getFieldsValue();
    const period = buildPeriodFromForm(values);
    return buildOrgPaymentAdviceParams({
      organizationId,
      period,
      claimSegment: values.claim_segment,
      filters: {
        search: values.search?.trim() || '',
      },
      reportOptions: values.report_options,
      outputFormat: values.output_format,
    });
  };

  const handlePreview = async () => {
    if (!organizationId) return;
    setPreviewLoading(true);
    setPreview(null);
    try {
      const params = buildParams();
      const data = await previewOrgPaymentAdvice(params);
      setPreview(data);
    } catch (e) {
      notification.error({
        message: 'Could not check totals',
        description: e?.message,
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleQueueExport = async () => {
    if (!organizationId) return;
    setExportLoading('queue');
    try {
      const params = buildParams();
      await queueOrgPaymentAdviceExport(params);
      notification.success({
        message: 'Report queued',
        description: 'Open Claims exports to download when it is ready.',
      });
      onOpenExportsDrawer?.();
    } catch (e) {
      notification.error({
        message: 'Could not generate report',
        description: e?.message,
      });
    } finally {
      setExportLoading(null);
    }
  };

  const handleDirectDownload = async (formatOverride) => {
    if (!organizationId) return;
    const values = form.getFieldsValue();
    const outputFormat = formatOverride || values.output_format || 'xlsx';
    setExportLoading(outputFormat);
    try {
      const params = buildParams();
      if (formatOverride) {
        params.output_format = formatOverride;
      }

      if (isServerRenderedOutputFormat(params.output_format)) {
        await fetchOrgPaymentAdviceDataSync(params);
      } else if (formatOverride === 'xlsx' || formatOverride === 'pdf') {
        params.output_format = 'json';
        const payload = await lib.fetchOrgPaymentAdviceData(params);
        if (payload?.data?.error) throw new Error(payload.data.message || 'Failed to load data');
        await renderOrgPaymentAdviceFromPayload(payload?.data?.data, formatOverride);
      } else {
        const result = await fetchOrgPaymentAdviceDataSync(params);
        if (result?.serverRendered) return;
        await renderOrgPaymentAdviceFromPayload(result, outputFormat);
      }

      notification.success({
        message: 'Download started',
        description: 'Your report file is downloading.',
      });
    } catch (e) {
      if (e?.status === 413) {
        notification.warning({
          message: 'Report too large',
          description: 'Use Generate report for background processing.',
        });
      } else {
        notification.error({
          message: 'Download failed',
          description: e?.message,
        });
      }
    } finally {
      setExportLoading(null);
    }
  };

  const periodType = Form.useWatch('period_type', form);
  const claimSegment = Form.useWatch('claim_segment', form);
  const dataScope = Form.useWatch(['report_options', 'data_scope'], form);
  const outputFormat = Form.useWatch('output_format', form);
  const isPaScope = isPaApprovedScope({ data_scope: dataScope });

  const periodTypeOptions = useMemo(() => {
    if (!isPaScope) return PERIOD_TYPES_ALL;
    return PERIOD_TYPES_ALL.filter((p) => p.value !== 'payment_batch');
  }, [isPaScope]);

  useEffect(() => {
    if (isPaScope && periodType === 'payment_batch') {
      form.setFieldValue('period_type', 'month_range');
    }
    if (isPaScope) {
      form.setFieldValue(['report_options', 'period_anchor'], 'check_in');
      form.setFieldValue(['report_options', 'category_approved_cost_metric'], 'pa');
    }
  }, [isPaScope, periodType, form]);

  const summaryMetrics = useMemo(
    () => (preview ? getPaymentAdviceSummaryMetrics(preview) : []),
    [preview],
  );

  const reconciliation = useMemo(
    () => getReconciliationBlock(preview),
    [preview],
  );

  const financeHelp = useMemo(() => {
    const scope = dataScope || reportOptionsDefaults?.data_scope;
    const schemaHelp = getSchemaHelpForScope(schema, scope);
    const previewHelp = getFinanceHelpBlock(preview);
    return mergeFinanceHelp(previewHelp, schemaHelp);
  }, [preview, schema, dataScope, reportOptionsDefaults?.data_scope]);

  const openPaDashboardCompare = useCallback(() => {
    const formValues = form.getFieldsValue();
    let period;
    try {
      period = buildPeriodFromForm(formValues);
    } catch {
      period = null;
    }

    const params = normalizePaDashboardCompareParams(
      reconciliation?.equivalent_pa_dashboard_params,
      {
        organizationId,
        sharedFilters: reconciliation?.shared_filters,
        period,
        dataScope: formValues?.report_options?.data_scope,
      },
    );

    if (!params.org_id) {
      notification.warning({
        message: 'Organization missing',
        description: 'Could not determine organization for PA dashboard comparison.',
      });
      return;
    }

    navigate(buildPaDashboardCompareUrl(params));
  }, [navigate, reconciliation, organizationId, form]);

  if (!canExport) {
    return (
      <Alert
        type="warning"
        showIcon
        message="You do not have permission to export utilization reports for this organization."
      />
    );
  }

  if (!organizationId) {
    return <Alert type="info" showIcon message="Loading organization…" />;
  }

  const schemaOutputFormats = schema?.output_formats;
  const fileTypeOptions = Array.isArray(schemaOutputFormats) && schemaOutputFormats.length
    ? FILE_TYPE_OPTIONS.filter((o) => schemaOutputFormats.includes(o.value))
    : FILE_TYPE_OPTIONS;

  const showClientRenderButtons =
    preview?.delivery?.sync_allowed &&
    (outputFormat === 'json' || !isServerRenderedOutputFormat(outputFormat));

  const showServerSyncButtons =
    preview?.delivery?.sync_allowed && isServerRenderedOutputFormat(outputFormat);

  const canDownloadNow = showClientRenderButtons || showServerSyncButtons;

  return (
    <Spin spinning={schemaLoading}>
      <div className="opa-export">
        <header className="opa-export__header">
          <h2 className="opa-export__title">Utilization report</h2>
          <p className="opa-export__subtitle">
            Consumption and approved-claim breakdown for <strong>{orgName}</strong>.
            Configure the report, check totals, then download or queue for processing.
          </p>
          <FinanceHelpPanel help={financeHelp} className="opa-export__help" />
        </header>

        <div className="opa-export__layout">
          <div className="opa-export__config">
            <Form form={form} layout="vertical" requiredMark="optional" size="middle">
              <section className="opa-export__section">
                <h3 className="opa-export__section-title">Report setup</h3>
                <Form.Item
                  name={['report_options', 'data_scope']}
                  label="What should this report include?"
                  rules={[{ required: true }]}
                >
                  <Radio.Group
                    options={DATA_SCOPE_OPTIONS}
                    optionType="button"
                    buttonStyle="solid"
                  />
                </Form.Item>
                <Form.Item name="output_format" label="Download as" rules={[{ required: true }]}>
                  <Radio.Group
                    options={fileTypeOptions}
                    optionType="button"
                    buttonStyle="solid"
                  />
                </Form.Item>
                <Form.Item
                  name={['report_options', 'pa_line_statuses']}
                  label="PA item statuses to include"
                  rules={[
                    {
                      validator: (_, value) =>
                        Array.isArray(value) && value.length
                          ? Promise.resolve()
                          : Promise.reject(new Error('Select at least one status')),
                    },
                  ]}
                >
                  <Checkbox.Group
                    className="opa-export__status-row"
                    options={PA_LINE_STATUS_OPTIONS}
                  />
                </Form.Item>
              </section>

              <section className="opa-export__section">
                <h3 className="opa-export__section-title">Period &amp; filters</h3>
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="period_type" label="Period" rules={[{ required: true }]}>
                      <Select options={periodTypeOptions} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    {periodType === 'month' ? (
                      <Form.Item name="single_month" label="Month" rules={[{ required: true }]}>
                        <DatePicker picker="month" style={{ width: '100%' }} />
                      </Form.Item>
                    ) : null}
                    {periodType === 'month_range' ? (
                      <Form.Item name="month_range" label="Month range" rules={[{ required: true }]}>
                        <RangePicker picker="month" style={{ width: '100%' }} />
                      </Form.Item>
                    ) : null}
                    {periodType === 'date_range' ? (
                      <Form.Item name="date_range" label="Date range" rules={[{ required: true }]}>
                        <RangePicker style={{ width: '100%' }} />
                      </Form.Item>
                    ) : null}
                    {periodType === 'payment_batch' ? (
                      <Form.Item name="payment_batch" label="Payment batch" rules={[{ required: true }]}>
                        <Input placeholder="e.g. PMT-2026-05-001" />
                      </Form.Item>
                    ) : null}
                  </Col>
                </Row>

                {!isPaScope ? (
                  <Form.Item name="claim_segment" label="Payment status" rules={[{ required: true }]}>
                    <Select options={SEGMENT_OPTIONS} />
                  </Form.Item>
                ) : (
                  <p className="opa-export__section-hint">
                    PA-based reports use check-in dates and include encounters with matching
                    pre-authorization lines, whether or not a claim has been filed.
                  </p>
                )}

                <Form.Item name="search" label="Filter by patient or encounter (optional)">
                  <Input
                    prefix={<SearchOutlined />}
                    placeholder="Name, insurance number, or encounter code"
                    allowClear
                  />
                </Form.Item>

                {!isPaScope && claimSegment !== 3 ? (
                  <Alert
                    type="info"
                    showIcon
                    className="opa-export__status-banner"
                    message="Payment date filtering applies only when status is Paid."
                  />
                ) : null}
              </section>

              <Collapse
                bordered={false}
                className="opa-export__advanced"
                items={[
                  {
                    key: 'advanced',
                    label: 'Calculation settings',
                    children: isPaScope ? (
                      <p className="opa-export__section-hint">
                        For PA approvals, calculation settings are applied by the server
                        (PA line amounts, check-in dates). Use Check totals to see the applied scope.
                      </p>
                    ) : (
                      <Row gutter={16}>
                        <Col xs={24} sm={8}>
                          <Form.Item
                            name={['report_options', 'total_approved_pa_metric']}
                            label="Total approved PA"
                          >
                            <Select
                              options={[
                                { value: 'pa', label: 'Pre-authorization' },
                                { value: 'claims', label: 'Claims' },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item
                            name={['report_options', 'period_anchor']}
                            label="Date used for period"
                          >
                            <Select
                              options={[
                                { value: 'check_in', label: 'Check-in date' },
                                { value: 'payment_date', label: 'Payment date (Paid only)' },
                                { value: 'segment_date', label: 'CFO / paid date' },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item
                            name={['report_options', 'category_approved_cost_metric']}
                            label="Category amounts from"
                          >
                            <Select
                              options={[
                                { value: 'claims', label: 'Claims' },
                                { value: 'pa', label: 'Pre-authorization' },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    ),
                  },
                ]}
              />
            </Form>
          </div>

          <aside className="opa-export__aside">
            {preview ? (
              <div className="opa-export__summary-card">
                <h3 className="opa-export__summary-heading">Report totals</h3>
                <p className="opa-export__meta-line">
                  <strong>{preview.organization?.name || orgName}</strong>
                  <br />
                  {preview.scope?.data_scope_label || '—'}
                  {preview.scope?.pa_line_statuses_label
                    ? ` · ${preview.scope.pa_line_statuses_label}`
                    : ''}
                </p>

                {reconciliation?.user_summary ? (
                  <p className="finance-reconcile-hint">{reconciliation.user_summary}</p>
                ) : null}

                <FinanceReconciliationPanel
                  reconciliation={reconciliation}
                  onNavigateCompare={
                    organizationId ? openPaDashboardCompare : undefined
                  }
                  compareLabel="Open PA dashboard with equivalent filters"
                />

                <div className="opa-export__metrics">
                  {summaryMetrics.map((metric) => (
                    <SummaryMetric
                      key={metric.field}
                      label={metric.label}
                      value={metric.value}
                      wide={metric.wide}
                      currency={metric.currency}
                      helpText={metric.helpText}
                      primary={metric.primary}
                      deprecated={metric.deprecated}
                    />
                  ))}
                </div>
                {preview.delivery?.sync_allowed === false ? (
                  <Alert
                    type="warning"
                    showIcon
                    className="opa-export__status-banner"
                    message="Large report"
                    description={`${preview.summary?.total_encounters ?? 0} encounters — use Generate report, then collect from Claims exports.`}
                  />
                ) : (
                  <Alert
                    type="success"
                    showIcon
                    className="opa-export__status-banner"
                    message="Totals confirmed"
                    description="You can download now or generate in the background."
                  />
                )}
              </div>
            ) : (
              <div className="opa-export__summary-card opa-export__summary-card--empty">
                <div className="opa-export__summary-empty-icon" aria-hidden>
                  <CalculatorOutlined />
                </div>
                <h3 className="opa-export__summary-heading">Review before export</h3>
                <p className="opa-export__summary-hint">
                  Check totals to verify enrollees, encounters, and amounts for your selected
                  period before downloading.
                </p>
              </div>
            )}

            <div className="opa-export__actions">
              <Button
                block
                icon={<CalculatorOutlined />}
                onClick={handlePreview}
                loading={previewLoading}
              >
                Check totals
              </Button>
              <Button
                block
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleQueueExport}
                loading={exportLoading === 'queue'}
              >
                Generate report
              </Button>
              {canDownloadNow && showServerSyncButtons ? (
                <Button
                  block
                  icon={outputFormat === 'pdf' ? <FilePdfOutlined /> : <FileExcelOutlined />}
                  onClick={() => handleDirectDownload()}
                  loading={exportLoading === outputFormat}
                >
                  Download {outputFormat === 'pdf' ? 'PDF' : 'Excel'} now
                </Button>
              ) : null}
              {canDownloadNow && showClientRenderButtons ? (
                <div className="opa-export__actions-row">
                  <Button
                    icon={<FileExcelOutlined />}
                    onClick={() => handleDirectDownload('xlsx')}
                    loading={exportLoading === 'xlsx'}
                  >
                    Excel
                  </Button>
                  <Button
                    icon={<FilePdfOutlined />}
                    onClick={() => handleDirectDownload('pdf')}
                    loading={exportLoading === 'pdf'}
                  >
                    PDF
                  </Button>
                </div>
              ) : null}
              <p className="opa-export__actions-note">
                <FileTextOutlined /> Large reports are processed in the background via Claims exports.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </Spin>
  );
};

export default OrgPaymentAdviceExportPanel;
